export type SubscriptionPlan =
  | "free"
  | "standard"
  | "pro"
  | "proplus"
  | "premium";
export type SubscriptionStatus =
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "inactive";
export type BillingInterval = "month" | "year" | null;
export type BillingCurrency = "usd" | "orion_token" | null;

export interface ISubscription {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  interval: BillingInterval;
  currency: BillingCurrency;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface CheckoutSessionDetails {
  plan: Exclude<SubscriptionPlan, "free">;
  interval: Exclude<BillingInterval, null>;
  amount: number; // in cents
  currency: string;
  email: string | null;
  status: "complete" | "expired" | "open";
}

export interface Invoice {
  id: string;
  amount_paid: number; // in cents (divide by 100 for display)
  currency: string;
  status: string;
  created: string; // ISO date string
  invoice_pdf: string | null;
  hosted_invoice_url: string | null;
}

export interface CreditSessionResponse {
  credits: number;
  amountUsd: number;
  creditAmountUsd: number;
  paymentStatus: string;
  createdAt: string;
}

/**
 * Trial state for a free-plan user (free-trial-contract@2 §4).
 * Populated only for free-plan users with a valid trial node; null/absent otherwise.
 */
export interface CreditBalanceTrial {
  active: boolean; // BE formula: now < endsAt && spendUsedUsd < spendLimitUsd — credit-only in @2 (endsAt is a far-future sentinel, so the time term never trips)
  endsAt: string; // ISO — far-future sentinel in @2 (e.g. 2099-01-01); NEVER render anything derived from it (no countdown, no deadline)
  spendUsedUsd: number; // cumulative trial quota spend (USD) — credit-only, no time window
  spendLimitUsd: number; // = 1.98 (60 trial credits × CREDIT_AI_RATE_USD; matches oa-agent TRIAL_CREDIT_BUDGET_USD)
  creditsRemaining: number; // max(0, (spendLimitUsd - spendUsedUsd) / CREDIT_AI_RATE_USD), round1 — DISPLAY value
  locked?: boolean; // operator hard-lock (node.lockedAt present) — A1 denies regardless of time gate; subscribing will NOT un-lock
  bypassed?: boolean; // operator exemption (node.bypassEnforcement === true) — A1 ALLOWS regardless of active; never wall
}

/** Add-on credit state (free-trial-contract@2 §4). */
export interface CreditBalanceAddOns {
  balance: number; // = availableCredits, surfaced explicitly
  spendable: boolean; // false unless stripe.status ∈ {active, trialing}
  frozenReason: "no_active_subscription" | null;
}

export interface CreditBalanceResponse {
  availableCredits: number;
  planCredits: number;
  currentPeriodSpendCredits: number;
  currentPeriodEnd: string | null;
  // ── NEW (all optional/nullable — absent on old BE, FE must handle absence) ──
  trial?: CreditBalanceTrial | null; // null = paid user, or free user with no minted trial yet
  addOns?: CreditBalanceAddOns;
  enforced?: boolean; // single source of truth for hard-wall vs shadow countdown
}

export const DEFAULT_SUBSCRIPTION: ISubscription = {
  plan: "free",
  status: "inactive",
  interval: null,
  currency: null,
  current_period_end: null,
  cancel_at_period_end: false,
  stripe_customer_id: null,
  stripe_subscription_id: null,
};
